<script setup>
import { useTemplateRef, onBeforeUnmount, onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { filterTypes } = useContactFilterContext();
const isMobile = useMediaQuery('(max-width: 639px)');
const panelRef = useTemplateRef('panelRef');
onMounted(() => panelRef.value?.focus());
const closeWithKeyboard = () => {
  if (props.saving) return;
  emit('close');
  document.getElementById('toggleContactsFilterButton')?.focus();
};

const filters = defineModel({
  type: Array,
  default: [],
});
const segmentNameLocal = ref(props.segmentName);

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  emit('clearFilters');
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedSegment = () => {
  if (props.saving) return;
  if (isConditionsValid()) {
    emit('updateSegment', filters.value, segmentNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) return;

  store.dispatch(
    'contacts/setContactFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => {
    if (!props.saving) emit('close');
  },
  { ignore: ['#toggleContactsFilterButton'] },
];
</script>

<template>
  <div
    ref="panelRef"
    v-on-click-outside="outsideClickHandler"
    tabindex="-1"
    role="region"
    :aria-label="filterModalHeaderTitle"
    data-testid="contacts-filter-panel"
    class="z-40 max-w-3xl min-w-0 sm:min-w-96 lg:w-[750px] max-h-[75dvh] overflow-y-auto w-full border border-n-weak bg-n-surface-1 shadow-lg rounded-xl p-4 sm:p-6 grid gap-4 sm:gap-6"
    @keydown.esc.stop.prevent="closeWithKeyboard"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
    <fieldset :disabled="saving" class="contents">
      <div v-if="props.isSegmentView">
        <div class="pb-6 border-b border-n-weak">
          <Input
            v-model="segmentNameLocal"
            :label="$t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
            :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
          />
        </div>
      </div>
      <ul class="grid gap-4 list-none">
        <template v-for="(filter, index) in filters" :key="filter.id">
          <ConditionRow
            v-if="index === 0"
            ref="conditionsRef"
            :key="`filter-${filter.attributeKey}-0`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :filter-types="filterTypes"
            :contained="isMobile"
            :show-query-operator="false"
            @remove="removeFilter(index)"
          />
          <ConditionRow
            v-else
            :key="`filter-${filter.attributeKey}-${index}`"
            ref="conditionsRef"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:query-operator="filters[index - 1].queryOperator"
            v-model:values="filter.values"
            show-query-operator
            :filter-types="filterTypes"
            :contained="isMobile"
            @remove="removeFilter(index)"
          />
        </template>
      </ul>
      <div
        class="flex flex-wrap justify-between gap-2 [&_button]:min-h-11 [&_button]:rounded-xl"
      >
        <Button sm ghost blue class="flex-shrink-0" @click="addFilter">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER') }}
        </Button>
        <div class="flex flex-wrap gap-2">
          <Button sm faded slate @click="resetFilter">
            {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
          </Button>
          <Button
            v-if="isSegmentView"
            sm
            solid
            blue
            :disabled="saving || !segmentNameLocal.trim()"
            :is-loading="saving"
            @click="updateSavedSegment"
          >
            {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT') }}
          </Button>
          <Button v-else sm solid blue @click="validateAndSubmit">
            {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS') }}
          </Button>
        </div>
      </div>
    </fieldset>
  </div>
</template>
